import { Action, createReducer, on } from '@ngrx/store';
import { SupplierRequestDetailV1 } from 'src/app/api/models';
import {
  clearError,
  loadRequest,
  loadRequestFailed,
  loadRequestSuccess,
  resetRequestForm,
  saveExternal,
  saveExternalFailed,
  saveExternalSuccess,
  saveInternal,
  saveInternalFailed,
  saveInternalSuccess,
  submitForExternalReview,
  submitForExternalReviewFailed,
  submitForExternalReviewSuccess,
  submitForInternalReview,
  submitForInternalReviewFailed,
  submitForInternalReviewSuccess,
  updateErpSyncStatus,
  updateErpSyncStatusFailed,
  updateErpSyncStatusSuccess,
} from '../../actions/request-form/request-form.actions';

export interface RequestFormState {
  loadingRequest: boolean;
  loadedRequestId?: string;
  request?: SupplierRequestDetailV1;

  saving: boolean;
  submitting: boolean;
  updatingErpSync: boolean;
  lastSavedRequestId?: string;
  lastSubmittedRequestId?: string;

  error?: unknown;
}

export const initialState: RequestFormState = {
  loadingRequest: false,
  loadedRequestId: undefined,
  request: undefined,

  saving: false,
  submitting: false,
  updatingErpSync: false,
  lastSavedRequestId: undefined,
  lastSubmittedRequestId: undefined,

  error: undefined,
};

const r = createReducer(
  initialState,

  on(loadRequest, (state, { requestId }) => ({
    ...state,
    loadingRequest: true,
    loadedRequestId: requestId,
    error: undefined,
  })),
  on(loadRequestSuccess, (state, { requestId, request }) => ({
    ...state,
    loadingRequest: false,
    loadedRequestId: requestId,
    request,
    error: undefined,
  })),
  on(loadRequestFailed, (state, { error }) => ({
    ...state,
    loadingRequest: false,
    error,
  })),

  on(saveInternal, (state) => ({
    ...state,
    saving: true,
    error: undefined,
  })),
  on(saveInternalSuccess, (state, { requestId }) => ({
    ...state,
    saving: false,
    lastSavedRequestId: requestId,
    error: undefined,
  })),
  on(saveInternalFailed, (state, { error }) => ({
    ...state,
    saving: false,
    error,
  })),

  on(submitForInternalReview, (state) => ({
    ...state,
    submitting: true,
    error: undefined,
  })),
  on(submitForInternalReviewSuccess, (state, { requestId }) => ({
    ...state,
    submitting: false,
    lastSubmittedRequestId: requestId,
    error: undefined,
  })),
  on(submitForInternalReviewFailed, (state, { error }) => ({
    ...state,
    submitting: false,
    error,
  })),

  on(saveExternal, (state) => ({
    ...state,
    saving: true,
    error: undefined,
  })),
  on(saveExternalSuccess, (state, { requestId }) => ({
    ...state,
    saving: false,
    lastSavedRequestId: requestId,
    error: undefined,
  })),
  on(saveExternalFailed, (state, { error }) => ({
    ...state,
    saving: false,
    error,
  })),

  on(submitForExternalReview, (state) => ({
    ...state,
    submitting: true,
    error: undefined,
  })),
  on(submitForExternalReviewSuccess, (state, { requestId }) => ({
    ...state,
    submitting: false,
    lastSubmittedRequestId: requestId,
    error: undefined,
  })),
  on(submitForExternalReviewFailed, (state, { error }) => ({
    ...state,
    submitting: false,
    error,
  })),

  on(updateErpSyncStatus, (state) => ({
    ...state,
    updatingErpSync: true,
    error: undefined,
  })),
  // Merge the returned status and reference into the loaded detail so the header updates without a reload.
  on(updateErpSyncStatusSuccess, (state, { requestId, request }) => ({
    ...state,
    updatingErpSync: false,
    request:
      state.request && state.loadedRequestId === requestId
        ? {
            ...state.request,
            status: request.status ?? state.request.status,
            requestErpReference: request.requestErpReference,
          }
        : state.request,
    error: undefined,
  })),
  on(updateErpSyncStatusFailed, (state, { error }) => ({
    ...state,
    updatingErpSync: false,
    error,
  })),

  on(clearError, (state) => ({
    ...state,
    error: undefined,
  })),

  on(resetRequestForm, () => initialState)
);

export function reducer(state: RequestFormState | undefined, action: Action) {
  return r(state, action);
}
